import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import { Form, Divider, Input, Select, Radio } from "semantic-ui-react";
import TourLayoutWrapper from "../components/TourLayoutWrapper";
import TourNav from "../constants/TourNav";
import VideoSlideshowProgramMode from "~/constants/Tour/VideoSlideshowProgramMode";
import PanningTime from "~/constants/Tour/PanningTime";
import SaveButton from "../components/SaveButton";
import TourStore from "~/stores/TourStore";
import ModalUnsavedConfirmation from "~/components/element/ModalUnsavedConfirmation";
import LoadingWrapper from "~/components/wrapper/LoadingWrapper";
import ModalError from "~/components/element/ModalError";
import FormStore from "~/stores/FormStore";
import FormHelper from "~/utils/FormHelper";
import ContentLoader from "~/components/element/ContentLoader";
import "../styles/MediaSettings.less";
import StickyMenu from "~/components/StickyMenu";
import AudioStore from "~/stores/AudioStore";
import Audio from "~/constants/Audio";
import MusicSelector from "~/components/MusicSelector";
import ToastMessageStore from "~/stores/ToastMessageStore";

@observer
class MediaSettingsPage extends Component {
  @observable formStore = null;

  constructor(props) {
    super(props);
    document.title = "Tour - Media Settings";

    this.formStore = new FormStore();
  }

  componentDidMount() {
    const { tourStore, audioStore, tourId, showError } = this.props;

    tourStore.load(tourId).then(() => {
      const { tour } = tourStore;
      this.bindTourToForm(tour);
    });

    if (!audioStore.audios.length) {
      audioStore.getAll({ inLibrary: 1, notAudioNone: 1 }).catch(error => showError(error.message));
    }

    window.scrollTo(0, 0);
  }

  @computed
  get audioDropDownOptions() {
    const { tourStore, audioStore, t } = this.props;
    const { tour } = tourStore;
    const { audios } = audioStore;

    const customerDefaultAudioOption =
      tour.customerDefaults && tour.customerDefaults.audioOption ? tour.customerDefaults.audioOption : "";

    const audioDropDownOptions = [
      {
        value: FormHelper.convertToString(null),
        key: "default",
        text: t("useDefaultWithValue", {
          defaultValue: ["none", "custom"].includes(customerDefaultAudioOption)
            ? t(`${customerDefaultAudioOption}`)
            : customerDefaultAudioOption,
        }),
      },
      {
        value: FormHelper.convertToString(Audio.CASCADING_NO_AUDIO_ID),
        key: FormHelper.convertToString(Audio.CASCADING_NO_AUDIO_ID),
        text: `[ ${t("clientPanelMisc:noAudio")} ]`,
      },
    ];

    audios.forEach(audio => {
      audioDropDownOptions.push({
        value: FormHelper.convertToString(audio.audioId),
        key: FormHelper.convertToString(audio.audioId),
        text: audio.compactDescription,
      });
    });

    return audioDropDownOptions;
  }

  @computed
  get tourCustomerDefaultVideoSlideshowProgramModeText() {
    const { tourStore, t } = this.props;
    const { tour } = tourStore;

    if (tour.customerDefaults && tour.customerDefaults.videoSlideshowProgramMode) {
      const defaultOption = VideoSlideshowProgramMode().find(
        option => option.value === tour.customerDefaults.videoSlideshowProgramMode,
      );

      if (typeof defaultOption === "undefined") {
        return tour.customerDefaults.videoSlideshowProgramMode;
      }

      return t(defaultOption.text);
    }

    return "";
  }

  @computed
  get tourCustomerDefaultPanningTimeText() {
    const { tourStore, t } = this.props;
    const { tour } = tourStore;

    if (tour.customerDefaults && tour.customerDefaults.panningTime) {
      const defaultOption = PanningTime().find(option => option.value === tour.customerDefaults.panningTime);

      if (typeof defaultOption === "undefined") {
        return tour.customerDefaults.panningTime;
      }

      return t(defaultOption.text);
    }

    return "";
  }

  bindTourToForm(tour) {
    const formData = {
      audioId: tour.audioId,
      slideshowTime: tour.slideshowTime,
      autoplay: tour.autoplay,
      videoSlideshowMode: tour.videoSlideshowMode,
      videoSlideshowProgramMode: tour.videoSlideshowProgramMode,
      panningTime: tour.panningTime,
      panningDuration: tour.panningDuration,
    };

    const denormalizedFormData = FormHelper.denormalizeData(formData);
    this.formStore = new FormStore(denormalizedFormData);
  }

  saveTour() {
    const { t, tourStore, tourId, toastMessageStore } = this.props;
    const { model } = this.formStore;
    const normalizedData = FormHelper.normalizeData({
      audioId: model.audioId,
      slideshowTime: model.slideshowTime,
      autoplay: model.autoplay,
      videoSlideshowMode: model.videoSlideshowMode,
      videoSlideshowProgramMode: model.videoSlideshowProgramMode,
      panningTime: Number.isNaN(Number(model.panningTime)) ? null : parseInt(model.panningTime, 10),
      panningDuration: Number.isNaN(Number(model.panningDuration)) ? null : parseInt(model.panningDuration, 10),
    });

    return tourStore.saveTour(tourId, normalizedData).then(() => {
      const { tour } = tourStore;
      this.bindTourToForm(tour);
      toastMessageStore.addToastMessage(t("changesSaved"), "positive", t("success"));
    });
  }

  render() {
    const { /** @var {TourStore} */ tourStore, audioStore, t, tourLayoutContentContainerRef } = this.props;
    const { tour } = tourStore;
    const { model, setProperty, bindInput, isDirty } = this.formStore;
    const { audios } = audioStore;

    if (tourStore.isLoading || audioStore.isLoading) {
      return <ContentLoader />;
    }

    let customPanTimeText = "";
    if (tour.customerDefaults) {
      if (tour.customerDefaults.panningDuration === 0) {
        customPanTimeText = t("clientPanelMisc:autoViewEntirePanoOnce");
      } else {
        customPanTimeText = t("clientPanelMisc:tourDurationSeconds", {
          tourPanningDuration: tour.customerDefaults.panningDuration,
        });
      }
    }

    const hasCustomPanningDuration = model.panningDuration !== "--null--" && model.panningDuration !== 0;

    return (
      <>
        <ModalUnsavedConfirmation when={isDirty} onSave={() => this.saveTour()} onDiscard={() => tourStore.reset()} />
        <LoadingWrapper loading={tourStore.isSaving} message={t("saving")} />
        <ModalError nestStore={tourStore} />

        <StickyMenu
          contextRef={tourLayoutContentContainerRef}
          headerText={t("clientPanelMisc:mediaSettings")}
          button={<SaveButton disabled={!isDirty} onClick={() => this.saveTour()} />}
        />

        <Form className="media-settings-form">
          <div className="media-settings-row">
            <Form.Field
              control={Select}
              label={t("music")}
              value={model.audioId}
              options={this.audioDropDownOptions}
              onChange={(_event, { value }) => setProperty("audioId", value)}
            />
            <div
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <MusicSelector
                audios={audios}
                newId={model.audioId}
                nullValue={
                  tour.customerDefaults && tour.customerDefaults.audioId
                    ? tour.customerDefaults.audioId
                    : Audio.CASCADING_NO_AUDIO_ID
                }
              />
            </div>
          </div>
          <Divider />

          <div className="media-settings-row">
            <div className="media-settings-radio-wrap">
              <div className="field">
                <label>{t("clientPanelMisc:imageDisplayTime")}</label>
              </div>
              <Form.Field
                label={t("clientPanelMisc:useDefaultDurationSeconds", {
                  customerDefaultSlideshowTime: tour.customerDefaults ? tour.customerDefaults.slideshowTime : "",
                })}
                control={Radio}
                name="slideshowTime"
                checked={model.slideshowTime === "--null--"}
                onChange={() => setProperty("slideshowTime", "--null--")}
              />
              <Form.Field
                label={t("custom")}
                control={Radio}
                name="slideshowTime"
                checked={model.slideshowTime !== "--null--"}
                onChange={() =>
                  setProperty("slideshowTime", tour.customerDefaults ? tour.customerDefaults.slideshowTime : "--null--")
                }
              />
              <Input
                {...bindInput("slideshowTime")}
                style={{ display: model.slideshowTime === "--null--" ? "none" : "block" }}
              />
            </div>
            <Form.Field
              control={Select}
              label={t("autoPlay")}
              value={model.autoplay}
              options={[
                {
                  key: "default",
                  value: "--null--",
                  text: t("useDefaultWithValue", {
                    defaultValue: tour.customerDefaults && tour.customerDefaults.autoplay ? t("yes") : t("no"),
                  }),
                },
                { key: "yes", value: "true", text: t("yes") },
                { key: "no", value: "false", text: t("no") },
              ]}
              onChange={(_event, { value }) => setProperty("autoplay", value)}
            />
          </div>
          <Divider />

          <div className="media-settings-row">
            <Form.Field
              control={Select}
              label={t("clientPanelMisc:panZoomEffect")}
              value={model.videoSlideshowMode}
              options={[
                {
                  key: "default",
                  value: "--null--",
                  text: t("useDefaultWithValue", {
                    defaultValue:
                      tour.customerDefaults && tour.customerDefaults.videoSlideshowMode ? t("on") : t("off"),
                  }),
                },
                { key: "yes", value: "true", text: t("on") },
                { key: "no", value: "false", text: t("off") },
              ]}
              onChange={(_event, { value }) => setProperty("videoSlideshowMode", value)}
            />

            <Form.Field
              control={Select}
              label={t("clientPanelMisc:panZoomEffectDirection")}
              value={model.videoSlideshowProgramMode}
              options={VideoSlideshowProgramMode([
                "useDefaultWithValue",
                {
                  defaultValue: this.tourCustomerDefaultVideoSlideshowProgramModeText,
                },
              ]).map(mode => {
                return {
                  key: mode.key,
                  value: mode.value,
                  text: typeof mode.text === "object" ? t(mode.text[0], mode.text[1]) : t(mode.text),
                };
              })}
              onChange={(_event, { value }) => setProperty("videoSlideshowProgramMode", value)}
            />
          </div>
          <Divider />

          <div className="media-settings-row">
            <Form.Field
              control={Select}
              label={t("clientPanelMisc:panoramaPanTime")}
              value={model.panningTime}
              options={PanningTime([
                "useDefaultWithValue",
                {
                  defaultValue: this.tourCustomerDefaultPanningTimeText,
                },
              ]).map(mode => {
                return {
                  key: mode.key,
                  value: mode.value,
                  text: typeof mode.text === "object" ? t(mode.text[0], mode.text[1]) : t(mode.text),
                };
              })}
              onChange={(_event, { value }) => setProperty("panningTime", value)}
            />

            <div className="media-settings-radio-wrap">
              <div className="field">
                <label>{t("clientPanelMisc:panoramaPanDuration")}</label>
              </div>
              <Form.Field
                control={Radio}
                label={t("useDefaultWithValue", { defaultValue: customPanTimeText })}
                name="panningDuration"
                checked={model.panningDuration === "--null--"}
                onChange={() => setProperty("panningDuration", "--null--")}
              />
              <Form.Field
                control={Radio}
                value={0}
                label={t("clientPanelMisc:autoViewEntirePanoOnce")}
                name="panningDuration"
                checked={model.panningDuration === 0}
                onChange={() => setProperty("panningDuration", 0)}
              />
              <Form.Field
                control={Radio}
                label={t("custom")}
                name="panningDuration"
                checked={hasCustomPanningDuration}
                // set panningDuration to 3 only if the user hasn't set a custom value already
                onChange={() => !hasCustomPanningDuration && setProperty("panningDuration", 3)}
              />
              <Input
                {...bindInput("panningDuration")}
                style={{
                  display: model.panningDuration === "--null--" || model.panningDuration === 0 ? "none" : "block",
                }}
              />
            </div>
          </div>
        </Form>
      </>
    );
  }
}

MediaSettingsPage.propTypes = {
  tourStore: PropTypes.instanceOf(TourStore),
  audioStore: PropTypes.instanceOf(AudioStore),
  tourId: PropTypes.number.isRequired,
  t: PropTypes.func.isRequired,
  tourLayoutContentContainerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  toastMessageStore: PropTypes.instanceOf(ToastMessageStore),
};

export default withTranslation()(TourLayoutWrapper(MediaSettingsPage, TourNav.MEDIA_SETTINGS));
